import { cloneDeep } from 'lodash';

import { Box } from '@hivebrite/design-system/Box';
import { IconList } from '@hivebrite/design-system/Icon';

import type { EmailListItemAttributes } from 'hb-builder/components/BuilderComponents/dynamic/EmailList/types';
import BuilderThemeProvider, {
  VIEW_AS,
} from 'hb-builder/components/common/BuilderThemeProvider';
import { defaultTabs } from 'hb-builder/constants/tabs';
import {
  useComponentContext,
  useComponentsContext,
  useRendererContext,
} from 'hb-builder/hooks';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import { APP_TYPE } from 'hb-react/types/appType';

import { INITIAL_DYNAMIC_ATTRIBUTES } from './constants';
import EmailListLayouts from './EmailListLayouts';
import EmailListSettings from './EmailListSettings';
import EmptyEmailList from './EmptyEmailList';

const EmailList = () => <div />; // never used

const RenderBuilder = () => {
  const { item } = useComponentContext();
  const { components } = useComponentsContext();

  const attributes = item?.attributes;
  const { fontFamily, type } = attributes?.dynamicAttributes || {};
  const { Section } = components;

  return (
    <Section.RenderBuilder attributes={attributes}>
      {type === 'empty' ? (
        <EmptyEmailList />
      ) : (
        <BuilderThemeProvider viewAs={VIEW_AS.USER}>
          <Box display="block" fontFamily={fontFamily} width="100%">
            <EmailListLayouts
              app={APP_TYPE.BACKOFFICE}
              dynamicAttributes={attributes?.dynamicAttributes}
              type={type}
            />
          </Box>
        </BuilderThemeProvider>
      )}
    </Section.RenderBuilder>
  );
};

// needed for the template preview thumbnails
const Render = ({ item }: { item: DocumentItem<EmailListItemAttributes> }) => {
  const { bodyWidth, isMobilePreview, workspaceConfig } = useRendererContext();
  const { components } = useComponentsContext();
  const { Section } = components;
  const { fontFamily, type } = item.attributes?.dynamicAttributes || {};

  if (type === 'empty' || !type) {
    return null;
  }

  return (
    <Section.Render
      bodyWidth={bodyWidth}
      isMobilePreview={isMobilePreview}
      item={item}
      workspaceConfig={workspaceConfig}
    >
      <Box display="block" fontFamily={fontFamily} width="100%">
        <EmailListLayouts
          app={APP_TYPE.PUBLIC_OFFICE}
          dynamicAttributes={item.attributes?.dynamicAttributes}
          type={type}
        />
      </Box>
    </Section.Render>
  );
};

EmailList.componentName = 'EmailList';
EmailList.componentType = 'section';
EmailList.componentLabel = __('List');
EmailList.componentCategory = 'dynamic';
EmailList.componentIcon = () => IconList;

EmailList.canBeDoubleClicked = true;
EmailList.canBeDragged = true;
EmailList.canBeListed = true;
EmailList.canBeSelected = true;

EmailList.item = ({ bodyWidth, components, features }) => {
  const { Section } = components;

  return Section.item({
    features: {
      ...features,
      section: {
        ...features?.section,
        category: EmailList.componentCategory,
        dynamicAttributes: cloneDeep(INITIAL_DYNAMIC_ATTRIBUTES),
        hasColGap: false,
        name: EmailList.componentName,
        numCols: 12,
        sectionWidth: bodyWidth,
        themeOverridden: true,
        type: EmailList.componentName,
      },
    },
  });
};

EmailList.toolbar = ['edit', 'drag', 'duplicate', 'delete'];

EmailList.Settings = EmailListSettings;
EmailList.SettingsTabs = defaultTabs;

EmailList.Render = Render;
EmailList.RenderBuilder = RenderBuilder;

export default EmailList;
